
import { PetCollection, PetCollectionItem, PetInventoryItem, PetSpecies, PetEgg, HatchingPotion } from '@/types';

// Food items for feeding pets to evolve them into mounts
export const FOOD_ITEMS = {
  basic_seed: { name: 'Basic Seed', icon: '🌱', feeding_power: 1 },
  premium_berry: { name: 'Premium Berry', icon: '🍓', feeding_power: 2 },
  energy_nectar: { name: 'Energy Nectar', icon: '🍯', feeding_power: 3 },
  golden_apple: { name: 'Golden Apple', icon: '🍎', feeding_power: 5 },
  magical_treat: { name: 'Magical Treat', icon: '✨', feeding_power: 10 },
  legendary_feast: { name: 'Legendary Feast', icon: '🍖', feeding_power: 15 }
};

// Random drops from activities
export const ACTIVITY_DROPS = {
  eggs: {
    common: ['wolf', 'tiger', 'panda', 'fox'],
    rare: ['dragon'],
    epic: ['phoenix', 'griffin', 'cerberus'],
    legendary: ['unicorn', 'hydra']
  },
  potions: {
    common: ['base', 'white', 'desert', 'red'],
    rare: ['shade'],
    epic: ['golden', 'zombie', 'skeleton'],
    legendary: ['cotton_candy_pink', 'cotton_candy_blue']
  },
  food: ['basic_seed', 'premium_berry', 'energy_nectar', 'golden_apple', 'magical_treat', 'legendary_feast']
};

// Drop rates based on activity points
export const getDropRates = (points: number) => {
  const baseRate = Math.min(points * 0.05, 0.8); // Max 80% drop rate
  return {
    egg: baseRate * 0.3,
    potion: baseRate * 0.3,
    food: baseRate * 0.4
  };
};

// Rarity chances
export const RARITY_CHANCES = {
  common: 0.6,
  rare: 0.25,
  epic: 0.12,
  legendary: 0.03
};

// Generate random drops from completing activities
export const generateActivityDrops = (activityPoints: number): PetInventoryItem[] => {
  const drops: PetInventoryItem[] = [];
  const dropRates = getDropRates(activityPoints);
  
  // Check for egg drop
  if (Math.random() < dropRates.egg) {
    const rarity = getRandomRarity();
    const eggSpecies = getRandomFromArray(ACTIVITY_DROPS.eggs[rarity]);
    drops.push({
      id: generateId(),
      user_id: '', // Will be set when added to user
      item_type: 'egg',
      item_key: eggSpecies,
      type: 'egg',
      quantity: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
  }

  // Check for potion drop
  if (Math.random() < dropRates.potion) {
    const rarity = getRandomRarity();
    const potionColor = getRandomFromArray(ACTIVITY_DROPS.potions[rarity]);
    drops.push({
      id: generateId(),
      user_id: '', // Will be set when added to user
      item_type: 'potion',
      item_key: potionColor,
      type: 'potion',
      quantity: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
  }

  // Check for food drop
  if (Math.random() < dropRates.food) {
    const foodItem = getRandomFromArray(ACTIVITY_DROPS.food);
    drops.push({
      id: generateId(),
      user_id: '', // Will be set when added to user
      item_type: 'food',
      item_key: foodItem,
      type: 'food',
      quantity: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
  }

  return drops;
};

// Create a pet from egg + potion combination
export const hatchPet = (egg: PetEgg, potion: HatchingPotion, speciesList: PetSpecies[]): PetCollectionItem | null => {
  const matchingSpecies = speciesList.find(s => 
    s.base_species === egg.species && 
    s.color === potion.color && 
    s.type === 'pet'
  );

  if (!matchingSpecies) return null;

  return {
    id: generateId(),
    user_id: '', // Will be set when added to user
    species_id: matchingSpecies.id,
    species: matchingSpecies,
    egg_id: egg.id,
    potion_id: potion.id,
    type: 'pet',
    food_fed: 0,
    food_needed: 50, // Default amount needed to become mount
    obtained: true,
    obtained_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
};

// Feed a pet to work towards mount evolution
export const feedPet = (pet: PetCollectionItem, foodKey: string, quantity: number = 1): PetCollectionItem => {
  const foodItem = FOOD_ITEMS[foodKey as keyof typeof FOOD_ITEMS];
  if (!foodItem) return pet;

  const feedingPower = foodItem.feeding_power * quantity;
  const newFoodFed = Math.min(pet.food_fed + feedingPower, pet.food_needed);

  return {
    ...pet,
    food_fed: newFoodFed,
    updated_at: new Date().toISOString()
  };
};

// Convert pet to mount when fully fed
export const evolvePetToMount = (pet: PetCollectionItem, speciesList: PetSpecies[]): PetCollectionItem | null => {
  if (pet.food_fed < pet.food_needed) return null;

  const mountSpecies = speciesList.find(s => 
    s.base_species === pet.species.base_species && 
    s.color === pet.species.color && 
    s.type === 'mount'
  );

  if (!mountSpecies) return null;

  return {
    ...pet,
    type: 'mount',
    species_id: mountSpecies.id,
    species: mountSpecies,
    mount_unlocked_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
};

// Check if egg + potion combination is valid
export const canHatchCombination = (egg: PetEgg, potion: HatchingPotion, speciesList: PetSpecies[]): boolean => {
  return speciesList.some(s => 
    s.base_species === egg.species && 
    s.color === potion.color && 
    s.type === 'pet'
  );
};

// Get all possible pet combinations
export const getAllPossiblePets = (speciesList: PetSpecies[]): Array<{species: string; color: string; type: 'pet' | 'mount'}> => {
  return speciesList.map(species => ({
    species: species.base_species,
    color: species.color,
    type: species.type
  }));
};

// Calculate collection completion percentage
export const calculateCollectionCompletion = (collection: PetCollection, speciesList: PetSpecies[]): {
  pets: { obtained: number; total: number; percentage: number };
  mounts: { obtained: number; total: number; percentage: number };
  overall: { obtained: number; total: number; percentage: number };
} => {
  const totalPets = speciesList.filter(s => s.type === 'pet').length;
  const totalMounts = speciesList.filter(s => s.type === 'mount').length;
  const totalOverall = totalPets + totalMounts;

  const obtainedPets = collection.pets.length;
  const obtainedMounts = collection.mounts.length;
  const obtainedOverall = obtainedPets + obtainedMounts;

  return {
    pets: {
      obtained: obtainedPets,
      total: totalPets,
      percentage: totalPets > 0 ? (obtainedPets / totalPets) * 100 : 0
    },
    mounts: {
      obtained: obtainedMounts,
      total: totalMounts,
      percentage: totalMounts > 0 ? (obtainedMounts / totalMounts) * 100 : 0
    },
    overall: {
      obtained: obtainedOverall,
      total: totalOverall,
      percentage: totalOverall > 0 ? (obtainedOverall / totalOverall) * 100 : 0
    }
  };
};

// Get mount bonuses for current equipped mount
export const getMountBonuses = (mount: PetCollectionItem | undefined): { xp_multiplier: number; bonus_type?: string } => {
  if (!mount?.species?.bonuses) {
    return { xp_multiplier: 1.0 };
  }

  return {
    xp_multiplier: mount.species.bonuses.xp_multiplier || 1.0,
    bonus_type: mount.species.bonuses.bonus_type
  };
};

// Helper functions
const getRandomRarity = (): 'common' | 'rare' | 'epic' | 'legendary' => {
  const rand = Math.random();
  if (rand < RARITY_CHANCES.legendary) return 'legendary';
  if (rand < RARITY_CHANCES.legendary + RARITY_CHANCES.epic) return 'epic';
  if (rand < RARITY_CHANCES.legendary + RARITY_CHANCES.epic + RARITY_CHANCES.rare) return 'rare';
  return 'common';
};

const getRandomFromArray = <T>(array: T[]): T => {
  return array[Math.floor(Math.random() * array.length)];
};

const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

// Initialize empty collection
export const createEmptyCollection = (userId: string): PetCollection => ({
  userId,
  pets: [],
  mounts: [],
  inventory: [],
  currentPet: undefined,
  currentMount: undefined,
  stats: {
    totalPets: 0,
    totalMounts: 0,
    totalEggs: 0,
    totalPotions: 0,
    rareCount: 0,
    epicCount: 0,
    legendaryCount: 0,
    collectionPercentage: 0,
  },
  discoveredSpecies: [],
  favoriteCreatures: [],
});

// Get pet/mount display info
export const getPetDisplayInfo = (pet: PetCollectionItem): {
  name: string;
  emoji: string;
  color: string;
  rarity: string;
  isMount: boolean;
  feedingProgress?: number;
} => {
  const species = pet.species.base_species || 'unknown';
  const color = pet.species.color || 'base';
  const rarity = pet.species.rarity || 'common';
  const isMount = pet.type === 'mount';
  
  // Simple emoji mapping - in real app, you'd have proper images
  const emojiMap: Record<string, string> = {
    wolf: '🐺',
    tiger: '🐅',
    panda: '🐼',
    fox: '🦊',
    dragon: '🐉',
    phoenix: '🔥',
    unicorn: '🦄',
    griffin: '🦅',
    hydra: '🐲',
    cerberus: '🐕'
  };

  return {
    name: pet.species.name || `${color} ${species}`,
    emoji: emojiMap[species] || '🐾',
    color,
    rarity,
    isMount,
    feedingProgress: isMount ? undefined : (pet.food_fed / pet.food_needed) * 100
  };
};
